/**
 * ステップエンジン
 *
 * シナリオの状態機械。ノード・ステップの遷移を管理します。
 * 分岐評価とカーソル管理は専用モジュールに委譲しています。
 */

import type { ScenarioContent, ScenarioNode, StepSchema, VariableMutation, LineConversationRecord, RouteMapHotspotActions } from 'shared';
import type { RunState } from 'shared';
import { createInitialRunState, normalizeRunState } from 'shared';
import { BranchEvaluator } from './branchEvaluator';
import { VariableValidator } from './variableValidator';
import { filterChoicesByCondition } from './choiceCondition';
import {
  readDialogCursor,
  writeDialogCursor,
  clearDialogCursor,
  readFreeInputReplyCursor,
  writeFreeInputReplyCursor,
  clearFreeInputReplyCursor,
} from './cursorManagement';

// ============================================
// 型定義
// ============================================

export type BranchStep = Extract<StepSchema, { type: 'branch' }>;
export type MinigameStep = Extract<StepSchema, { type: 'minigame' }>;
export type RewardStep = Extract<StepSchema, { type: 'reward' }>;
export type EndingStep = Extract<StepSchema, { type: 'ending' }>;
export type FreeInputStep = Extract<StepSchema, { type: 'free_input' }>;
export type SpotGateStep = Extract<StepSchema, { type: 'spot_gate' }>;

export interface EngineSnapshot {
  node: ScenarioNode | null;
  step: StepSchema | null;
  state: RunState;
  waiting: WaitingState | null;
  isCompleted: boolean;
}

export type DialogStep = Extract<StepSchema, { type: 'dialog' }>;

export type LineDialogStep = Extract<StepSchema, { type: 'line_dialog' }>;

/** R3: stats.minigameResults の1要素型（runState の定義から派生） */
type MinigameResultStat = NonNullable<NonNullable<RunState['stats']>['minigameResults']>[number];

export type WaitingState =
  | BranchWaitingState
  | MinigameWaitingState
  | SpotGateWaitingState
  | RewardWaitingState
  | FreeInputWaitingState
  | DialogChoiceWaitingState
  | LineDialogWaitingState;

export interface BranchWaitingState {
  type: 'branch';
  step: BranchStep;
  options: { goto: string; label?: string; expression?: string }[];
}

export interface DialogChoiceWaitingState {
  type: 'dialog_choice';
  step: DialogStep;
  choices: { text: string; goto?: string; mutations?: VariableMutation[]; condition?: unknown }[];
}

export interface MinigameWaitingState {
  type: 'minigame';
  step: MinigameStep;
}

export interface SpotGateWaitingState {
  type: 'spot_gate';
  step: SpotGateStep;
  attempts: number;
  remainingAttempts: number | null;
}

export interface RewardWaitingState {
  type: 'reward';
  step: RewardStep;
}

export interface FreeInputWaitingState {
  type: 'free_input';
  step: FreeInputStep;
}

export interface LineDialogWaitingState {
  type: 'line_dialog';
  step: LineDialogStep;
}

// ============================================
// StepEngine クラス
// ============================================

export class StepEngine {
  private state: RunState;
  private waiting: WaitingState | null;
  private completed = false;
  private readonly scenario: ScenarioContent;
  private readonly branchEvaluator: BranchEvaluator;
  private readonly variableValidator: VariableValidator | null;

  constructor(scenario: ScenarioContent, initialState?: RunState) {
    this.scenario = scenario;
    this.state = initialState
      ? normalizeRunState(initialState, scenario)
      : createInitialRunState(scenario);
    this.branchEvaluator = new BranchEvaluator(this.state);

    // Phase A-h: 変数バリデーター初期化
    if (scenario.variables?.definitions && scenario.variables.definitions.length > 0) {
      this.variableValidator = new VariableValidator(scenario.variables.definitions);
      // デフォルト値で変数を初期化
      this.state = {
        ...this.state,
        variables: this.variableValidator.initializeVariables(this.state.variables),
      };
    } else {
      this.variableValidator = null;
    }

    this.waiting = this.computeWaiting(this.getCurrentStep());
    if (!this.state.currentNodeId) {
      this.completed = true;
    }
  }

  getSnapshot(): EngineSnapshot {
    const node = this.getCurrentNode();
    const step = this.getCurrentStep();
    return {
      node,
      step,
      state: this.state,
      waiting: this.waiting,
      isCompleted: this.completed && (!node || !step),
    };
  }

  getWaitingState(): WaitingState | null {
    return this.waiting;
  }

  getState(): RunState {
    return this.state;
  }

  getScenario(): ScenarioContent {
    return this.scenario;
  }

  advance(): EngineSnapshot {
    if (this.completed) {
      return this.getSnapshot();
    }
    if (this.waiting) {
      throw new Error('Waiting for user input');
    }

    const current = this.getCurrentStep();

    // dialog: 1 Step内で複数ラリー（lines）を進める
    if (current && current.type === 'dialog') {
      const lines = Array.isArray((current as any).lines)
        ? ((current as any).lines as any[])
        : null;
      if (lines && lines.length > 0) {
        const cursor = readDialogCursor(this.state.flags);
        const currentIndex = cursor && cursor.stepId === current.id ? cursor.index : 0;
        if (currentIndex < lines.length - 1) {
          this.state = {
            ...this.state,
            flags: writeDialogCursor(this.state.flags, {
              stepId: current.id,
              index: currentIndex + 1,
            }),
          };
          return this.getSnapshot();
        }
        this.state = {
          ...this.state,
          flags: clearDialogCursor(this.state.flags),
        };
      }
      // Phase A-h: linesが終わったら、choicesがある場合は待機状態にする
      // R3: condition で表示する選択肢を絞る（現在の variables/flags で評価）。
      // 表示側に渡す choices は selectDialogChoice の index 解決と同一フィルタ
      // （filterChoicesByCondition）を通すため、index がズレない。
      const choices = (current as any).choices as Array<{
        text: string;
        goto?: string;
        mutations?: VariableMutation[];
        condition?: unknown;
      }> | undefined;
      if (choices && choices.length > 0) {
        const visibleChoices = filterChoicesByCondition(choices, this.state);
        this.waiting = {
          type: 'dialog_choice',
          step: current as DialogStep,
          choices: visibleChoices,
        };
        return this.getSnapshot();
      }
    }

    // free_input: 返答（複数発話）を 1 Step内で進める
    if (current && current.type === 'free_input') {
      const cursor = readFreeInputReplyCursor(this.state.flags);
      if (cursor && cursor.stepId === current.id) {
        if (cursor.index < cursor.lines.length - 1) {
          this.state = {
            ...this.state,
            flags: writeFreeInputReplyCursor(this.state.flags, {
              ...cursor,
              index: cursor.index + 1,
            }),
          };
          return this.getSnapshot();
        }
        this.state = {
          ...this.state,
          flags: clearFreeInputReplyCursor(this.state.flags),
        };
      }
    }

    // Phase A-h: 変数操作ステップを自動処理
    if (current && current.type === 'variable_mutation') {
      const vmStep = current as Extract<StepSchema, { type: 'variable_mutation' }>;
      this.applyMutations(vmStep.mutations);
      this.incrementStep();
      return this.getSnapshot();
    }

    // branchステップを自動評価
    if (current && current.type === 'branch') {
      const branchStep = current as BranchStep;
      this.branchEvaluator.updateState(this.state);
      const autoGoto = this.branchEvaluator.evaluateBranch(branchStep);
      const goto = autoGoto ?? branchStep.fallbackGoto;
      if (goto) {
        this.moveToTarget(goto);
        return this.getSnapshot();
      }
      // gotoが見つからない場合は次のステップに進む
      this.incrementStep();
      return this.getSnapshot();
    }

    this.incrementStep();
    // 2026-07-20 傷①: 他の進行メソッド（spot_gate/dialog choice/line_dialog/minigame）と対称に
    // 自動ステップ（variable_mutation/branch）を連続処理する。これが無いと「選択肢なし dialog の
    // 最終行送り」や「free_input 返答の読み終わり」の直後に variable_mutation があると、タップ送り
    // 対象外の step に waiting=null で着地して脱出手段ゼロで停止する
    // （hajimeteNoIrai node-inv-success: dialog lines → step-success-mutation が本線で該当）。
    this.autoAdvanceThroughAutoSteps();
    return this.getSnapshot();
  }

  /**
   * Phase A-h: 変数操作を適用
   */
  applyMutations(mutations: VariableMutation[]): void {
    if (!mutations || mutations.length === 0) {
      return;
    }

    let newVars = { ...this.state.variables };

    for (const mut of mutations) {
      if (this.variableValidator) {
        newVars = this.variableValidator.applyMutation(newVars, mut);
      } else {
        // バリデーターがない場合は単純に適用
        switch (mut.op) {
          case 'set':
            newVars[mut.key] = mut.value;
            break;
          case 'increment':
            newVars[mut.key] = Number(newVars[mut.key] ?? 0) + (mut.amount ?? 1);
            break;
          case 'decrement':
            newVars[mut.key] = Number(newVars[mut.key] ?? 0) - (mut.amount ?? 1);
            break;
          case 'toggle':
            newVars[mut.key] = !newVars[mut.key];
            break;
        }
      }
    }

    this.state = {
      ...this.state,
      variables: newVars,
    };
    this.branchEvaluator.updateState(this.state);
  }

  submitSpotGateAnswer(answer: string): EngineSnapshot {
    const waiting = this.ensureWaiting<SpotGateWaitingState>('spot_gate');
    const step = waiting.step;
    const nextAttempts = waiting.attempts + 1;
    // P8-1: 照合ロジックは evaluateSpotGateAnswer（純関数）へ一本化（挙動不変）。
    const isCorrect = evaluateSpotGateAnswer(step, answer);
    const attemptFlagKey = getSpotGateAttemptFlagKey(step.id);

    if (isCorrect) {
      this.applyMutations(((step as any).successMutations ?? []) as VariableMutation[]);
      const { [attemptFlagKey]: _attempts, ...restFlags } = this.state.flags as Record<string, unknown>;
      this.state = {
        ...this.state,
        flags: {
          ...restFlags,
          [`${step.id}_spot_gate_passed`]: true
        }
      };
      this.waiting = null;
      if ((step as any).next) {
        this.moveToTarget((step as any).next);
      } else {
        this.incrementStep();
      }
      this.autoAdvanceThroughAutoSteps();
      return this.getSnapshot();
    }

    const maxAttempts = typeof (step as any).maxAttempts === 'number' ? (step as any).maxAttempts : null;
    const reachedLimit = maxAttempts !== null && nextAttempts >= maxAttempts;

    if (reachedLimit) {
      this.applyMutations(((step as any).failMutations ?? []) as VariableMutation[]);
      const { [attemptFlagKey]: _attempts, ...restFlags } = this.state.flags as Record<string, unknown>;
      this.state = {
        ...this.state,
        flags: {
          ...restFlags,
          [`${step.id}_spot_gate_passed`]: false
        }
      };
      this.waiting = null;
      if ((step as any).next) {
        this.moveToTarget((step as any).next);
      } else {
        this.incrementStep();
      }
      this.autoAdvanceThroughAutoSteps();
      return this.getSnapshot();
    }

    this.state = {
      ...this.state,
      flags: {
        ...this.state.flags,
        [attemptFlagKey]: nextAttempts
      }
    };
    this.waiting = this.computeWaiting(step);
    return this.getSnapshot();
  }

  resolveBranch(targetNodeId?: string, choiceMutations?: VariableMutation[]): EngineSnapshot {
    const waiting = this.ensureWaiting<BranchWaitingState>('branch');

    // Phase A-h: 選択肢による変数変更を適用
    if (choiceMutations && choiceMutations.length > 0) {
      this.applyMutations(choiceMutations);
    }

    this.branchEvaluator.updateState(this.state);
    const autoGoto = this.branchEvaluator.evaluateBranch(waiting.step);
    const goto = targetNodeId ?? autoGoto ?? waiting.step.fallbackGoto;
    this.waiting = null;
    this.moveToTarget(goto);
    return this.getSnapshot();
  }

  /**
   * Phase A-h: ダイアログの選択肢を選択
   * 選択肢に紐づく変数変更を適用し、gotoが指定されていれば遷移する
   */
  selectDialogChoice(choiceIndex: number): EngineSnapshot {
    const current = this.getCurrentStep();
    if (!current || current.type !== 'dialog') {
      throw new Error('Not a dialog step');
    }

    const dialogStep = current as Extract<StepSchema, { type: 'dialog' }>;
    const allChoices = (dialogStep as any).choices as Array<{
      text: string;
      goto?: string;
      mutations?: VariableMutation[];
      condition?: unknown;
    }> | undefined;

    // R3: 表示側（overlay）が見ているのと同一のフィルタ済みリストに対して
    // index を解釈する。フィルタ前の全件配列に index を当てると、condition で
    // 隠れた選択肢があるときに別の選択肢を選んでしまう。
    const choices = filterChoicesByCondition(allChoices, this.state);

    if (!choices || choiceIndex < 0 || choiceIndex >= choices.length) {
      throw new Error('Invalid choice index');
    }

    const choice = choices[choiceIndex];

    // 変数変更を適用
    if (choice.mutations && choice.mutations.length > 0) {
      this.applyMutations(choice.mutations);
    }

    // 遷移先が指定されていれば遷移
    if (choice.goto) {
      this.moveToNode(choice.goto);
    } else {
      // 遷移先がなければ次のステップへ
      this.incrementStep();
    }

    // P8-0: 他の進行メソッド（spot_gate/line_dialog/minigame）と対称に自動ステップを連続処理する。
    // これが無いと goto なし choice の直後に variable_mutation があると、タップ送り・autoAdvance
    // どちらの対象にもならず脱出手段ゼロで停止する（soma Cycle 6 ソフトロックの根本原因）。
    this.autoAdvanceThroughAutoSteps();

    return this.getSnapshot();
  }

  /**
   * LINE Dialog の選択肢を選択、またはautoAdvanceで次へ進む
   */
  private recordLineDialogHistory(step: LineDialogStep, userReply?: string): void {
    const speakers = (step as any).speakers ?? {};
    const msgs = ((step as any).messages ?? []) as Array<{
      senderKey: string;
      text: string;
    }>;

    const record: LineConversationRecord = {
      stepId: step.id,
      chatTitle: (step as any).chatTitle ?? '',
      messages: msgs.map((m) => {
        const sp = speakers[m.senderKey];
        return {
          senderKey: m.senderKey,
          displayName: sp?.displayName ?? m.senderKey,
          text: m.text.length > 200 ? m.text.slice(0, 200) + '…' : m.text,
          side: (sp?.side ?? 'other') as 'other' | 'self'
        };
      }),
      userReply
    };

    const history = this.state.lineHistory ?? { conversations: [] };
    const conversations = [...history.conversations, record];
    // Max 100 conversations
    if (conversations.length > 100) {
      conversations.splice(0, conversations.length - 100);
    }
    this.state = {
      ...this.state,
      lineHistory: { conversations }
    };
  }

  selectLineDialogChoice(choiceIndex?: number): EngineSnapshot {
    const waiting = this.ensureWaiting<LineDialogWaitingState>('line_dialog');
    const step = waiting.step;
    const allOptions = (step as any).reply?.options as Array<{
      text: string;
      goto?: string;
      mutations?: VariableMutation[];
      condition?: unknown;
    }> | undefined;
    // R3: LineDialogView が表示するのと同一の condition フィルタ済みリストに
    // 対して index を解釈する（両者が filterChoicesByCondition を通すので一致）。
    const options = filterChoicesByCondition(allOptions, this.state);

    // Record history before transitioning
    const choiceText = (choiceIndex !== undefined && options && choiceIndex >= 0 && choiceIndex < options.length)
      ? options[choiceIndex].text
      : undefined;
    this.recordLineDialogHistory(step, choiceText);

    if (choiceIndex !== undefined && options && choiceIndex >= 0 && choiceIndex < options.length) {
      const choice = options[choiceIndex];
      if (choice.mutations && choice.mutations.length > 0) {
        this.applyMutations(choice.mutations);
      }
      this.waiting = null;
      if (choice.goto) {
        this.moveToTarget(choice.goto);
      } else {
        this.incrementStep();
      }
    } else {
      // autoAdvance or no choices: just move to next step
      this.waiting = null;
      this.incrementStep();
    }

    this.autoAdvanceThroughAutoSteps();
    return this.getSnapshot();
  }

  /**
   * LINE Dialog の自由入力結果を保存（Phase 1: 結果保存のみ、待機状態は維持）
   * AIの返信をフラグに保存し、LineDialogView がアニメーション表示する
   */
  storeLineDialogAiReply(userText: string, routeKey: string, aiReplyLines: string[]): EngineSnapshot {
    this.ensureWaiting<LineDialogWaitingState>('line_dialog');
    this.state = {
      ...this.state,
      flags: {
        ...this.state.flags,
        __lineDialogAiReply: { userText, routeKey, aiReplyLines },
      },
    };
    // 待機状態は維持（LineDialogViewがAI返信アニメーション後にcompleteLineDialogを呼ぶ）
    return this.getSnapshot();
  }

  /**
   * LINE Dialog を完了して次のステップへ進む（Phase 2: アニメーション完了後）
   */
  completeLineDialog(): EngineSnapshot {
    const waiting = this.ensureWaiting<LineDialogWaitingState>('line_dialog');
    // Record history with user's free input text
    const aiReplyData = this.state.flags.__lineDialogAiReply as { userText?: string } | undefined;
    this.recordLineDialogHistory(waiting.step, aiReplyData?.userText);
    // AIリプライフラグをクリア
    const { __lineDialogAiReply: _, ...restFlags } = this.state.flags as Record<string, unknown>;
    this.state = {
      ...this.state,
      flags: restFlags as Record<string, unknown>,
    };
    this.waiting = null;
    this.incrementStep();
    this.autoAdvanceThroughAutoSteps();
    return this.getSnapshot();
  }

  discoverRouteMapHotspot(spotId: string, hotspotId: string, actions?: RouteMapHotspotActions): EngineSnapshot {
    const mutations: VariableMutation[] = [];

    if (actions?.setVariables) {
      for (const [key, value] of Object.entries(actions.setVariables)) {
        mutations.push({ op: 'set', key, value } as VariableMutation);
      }
    }

    if (actions?.unlockClueIds) {
      for (const clueId of actions.unlockClueIds) {
        const clue = this.scenario.clues?.find((definition) => definition.id === clueId);
        const key = clue?.unlockVariable || `clue_${clueId}_unlocked`;
        mutations.push({ op: 'set', key, value: true } as VariableMutation);
      }
    }

    if (mutations.length > 0) {
      this.applyMutations(mutations);
    }

    if (actions?.addItemIds && actions.addItemIds.length > 0) {
      const current = this.state.stats?.collectedItemIds ?? [];
      this.state = {
        ...this.state,
        stats: {
          ...(this.state.stats ?? {}),
          collectedItemIds: Array.from(new Set([...current, ...actions.addItemIds]))
        }
      };
    }

    this.state = {
      ...this.state,
      flags: {
        ...this.state.flags,
        [getRouteMapHotspotFlagKey(spotId, hotspotId)]: true
      }
    };
    return this.getSnapshot();
  }

  completeMinigame(result: Record<string, unknown>): EngineSnapshot {
    const waiting = this.ensureWaiting<MinigameWaitingState>('minigame');
    const varKey = waiting.step.resultVarKey ?? 'lastMinigameResult';
    this.state = {
      ...this.state,
      variables: {
        ...this.state.variables,
        [varKey]: result,
      },
    };
    // R3: ミニゲーム成績を stats.minigameResults に1件追記（物語側の参照用）。
    // 1ミニゲーム1回しか呼ばれないため冪等性は不要。既存の stats は壊さない。
    this.appendMinigameResultStat(waiting.step, result);
    const mappingNext = this.applyMinigameResultMappings(waiting.step, result);
    this.waiting = null;
    if (mappingNext) {
      this.moveToTarget(mappingNext);
    } else {
      this.incrementStep();
    }
    // 自動処理可能なステップ（branch, variable_mutation）を連続処理
    this.autoAdvanceThroughAutoSteps();
    return this.getSnapshot();
  }

  /**
   * R3: 完了したミニゲームの成績を state.stats.minigameResults に1件追記する。
   * 物語側（reward の endingConditions・後続 dialog の choice.condition 等）が
   * 「何本クリアしたか」「perfect だったか」を参照できるようにするための土台。
   *
   * - templateId / registryId は step から、status/score/success/perfect/retryCount は
   *   result から取る（result は normalizeMinigameResult で success/retryCount/perfect 補完済）。
   * - score/perfect は result に存在する場合のみ格納（無い型のテンプレでは undefined のまま）。
   */
  private appendMinigameResultStat(step: MinigameStep, result: Record<string, unknown>): void {
    const entry: MinigameResultStat = {
      templateId: step.templateId,
      status: typeof result.status === 'string' ? result.status : (result.success === true ? 'success' : 'fail'),
    };
    if (step.registryId) {
      entry.registryId = step.registryId;
    }
    if (typeof result.success === 'boolean') {
      entry.success = result.success;
    }
    if (typeof result.score === 'number') {
      entry.score = result.score;
    }
    if (typeof result.perfect === 'boolean') {
      entry.perfect = result.perfect;
    }
    if (typeof result.retryCount === 'number') {
      entry.retryCount = result.retryCount;
    }

    const prevStats = this.state.stats ?? {};
    const prevResults = Array.isArray(prevStats.minigameResults) ? prevStats.minigameResults : [];
    this.state = {
      ...this.state,
      stats: {
        ...prevStats,
        minigameResults: [...prevResults, entry],
      },
    };
  }

  private applyMinigameResultMappings(step: MinigameStep, result: Record<string, unknown>): string | undefined {
    const mappings = ((step as any).resultMappings ?? []) as Array<{
      id: string;
      when?: {
        status?: 'success' | 'fail' | 'partial';
        minScore?: number;
        resultKey?: string;
        equals?: string | number | boolean;
      };
      unlockClues?: string[];
      setVariables?: Record<string, string | number | boolean>;
      addItems?: string[];
      next?: string;
    }>;
    if (!Array.isArray(mappings) || mappings.length === 0) {
      return undefined;
    }

    const matched = mappings.find((mapping) => matchesMinigameResultMapping(mapping.when ?? {}, result));
    if (!matched) {
      return undefined;
    }

    const mutations: VariableMutation[] = [];

    if (matched.setVariables) {
      for (const [key, value] of Object.entries(matched.setVariables)) {
        mutations.push({ op: 'set', key, value } as VariableMutation);
      }
    }

    if (matched.unlockClues) {
      for (const clueId of matched.unlockClues) {
        const clue = this.scenario.clues?.find((definition) => definition.id === clueId);
        const key = clue?.unlockVariable || `clue_${clueId}_unlocked`;
        mutations.push({ op: 'set', key, value: true } as VariableMutation);
      }
    }

    if (mutations.length > 0) {
      this.applyMutations(mutations);
    }

    if (matched.addItems && matched.addItems.length > 0) {
      const current = this.state.stats?.collectedItemIds ?? [];
      const collectedItemIds = Array.from(new Set([...current, ...matched.addItems]));
      this.state = {
        ...this.state,
        stats: {
          ...(this.state.stats ?? {}),
          collectedItemIds
        }
      };
    }

    return matched.next;
  }

  /**
   * 自動処理可能なステップ（branch, variable_mutation）を連続して処理する
   * ユーザー操作が必要なステップに到達するか、完了するまで進む
   */
  private autoAdvanceThroughAutoSteps(): void {
    const maxIterations = 100; // 無限ループ防止
    for (let i = 0; i < maxIterations; i++) {
      if (this.completed || this.waiting) {
        return;
      }

      const current = this.getCurrentStep();
      if (!current) {
        return;
      }

      // branch: 自動評価
      if (current.type === 'branch') {
        const branchStep = current as BranchStep;
        this.branchEvaluator.updateState(this.state);
        const autoGoto = this.branchEvaluator.evaluateBranch(branchStep);
        const goto = autoGoto ?? branchStep.fallbackGoto;
        if (goto) {
          this.moveToTarget(goto);
        } else {
          this.incrementStep();
        }
        continue;
      }

      // variable_mutation: 自動適用
      if (current.type === 'variable_mutation') {
        const vmStep = current as Extract<StepSchema, { type: 'variable_mutation' }>;
        this.applyMutations(vmStep.mutations);
        this.incrementStep();
        continue;
      }

      // その他のステップタイプは自動処理しない
      return;
    }

    console.warn('[StepEngine] autoAdvanceThroughAutoSteps reached max iterations');
  }

  submitFreeInput(routeKey: string, assistantLines: string[]): EngineSnapshot {
    const waiting = this.ensureWaiting<FreeInputWaitingState>('free_input');
    const flagKey = waiting.step.routeKeyFlagKey ?? 'routeKey';
    const safeLines =
      Array.isArray(assistantLines) && assistantLines.length > 0
        ? assistantLines
        : ['……'];
    this.state = {
      ...this.state,
      flags: writeFreeInputReplyCursor(
        {
          ...this.state.flags,
          [flagKey]: routeKey,
        },
        {
          stepId: waiting.step.id,
          index: 0,
          lines: safeLines,
        }
      ),
    };
    this.waiting = null;
    return this.getSnapshot();
  }

  completeReward(requestedEndingId?: string): EngineSnapshot {
    const waiting = this.ensureWaiting<RewardWaitingState>('reward');
    const rewardStep = waiting.step;

    // Phase A-h: エンディング条件を評価してエンディングIDを決定
    let finalEndingId = requestedEndingId;
    if (!finalEndingId && (rewardStep as any).endingConditions) {
      const endingConditions = (rewardStep as any).endingConditions as Array<{
        condition: unknown;
        endingId: string;
        title?: string;
      }>;
      this.branchEvaluator.updateState(this.state);

      for (const cond of endingConditions) {
        const result = this.branchEvaluator.applyBranchPreset(cond.condition as any);
        if (result) {
          finalEndingId = cond.endingId;
          break;
        }
      }
    }

    // デフォルトエンディング
    if (!finalEndingId && (rewardStep as any).defaultEnding) {
      finalEndingId = (rewardStep as any).defaultEnding.endingId;
    }

    // フォールバック
    if (!finalEndingId) {
      finalEndingId = rewardStep.id;
    }

    this.state = {
      ...this.state,
      flags: {
        ...this.state.flags,
        lastEndingId: finalEndingId,
      },
    };
    this.waiting = null;
    const action = rewardStep.nextAction ?? 'end';

    switch (action) {
      case 'replay': {
        this.state = createInitialRunState(this.scenario);
        this.completed = false;
        this.waiting = this.computeWaiting(this.getCurrentStep());
        return this.getSnapshot();
      }
      case 'next': {
        this.incrementStep();
        // 2026-07-20 傷①: advance()/他進行メソッドと対称に、直後の自動ステップ
        // （variable_mutation/branch）を連続処理してから返す（waiting=null で
        // 自動ステップに着地する恒久ソフトロックの防止）。
        this.autoAdvanceThroughAutoSteps();
        return this.getSnapshot();
      }
      case 'end':
      default:
        this.moveToNode(null);
        return this.getSnapshot();
    }
  }

  completeEnding(requestedEndingId?: string): EngineSnapshot {
    const endingStep = this.getCurrentStep();
    if (!endingStep || endingStep.type !== 'ending') {
      throw new Error('Not an ending step');
    }

    this.state = {
      ...this.state,
      flags: {
        ...this.state.flags,
        lastEndingId: requestedEndingId ?? (endingStep as EndingStep).endingId,
      },
    };
    return this.getSnapshot();
  }

  // ============================================
  // Private methods
  // ============================================

  private incrementStep(): void {
    const node = this.getCurrentNode();
    if (!node) {
      this.setCompleted();
      return;
    }

    const nextIndex = this.state.currentStepIndex + 1;
    if (node.steps && nextIndex < node.steps.length) {
      this.state = {
        ...this.state,
        currentStepIndex: nextIndex,
      };
      this.waiting = this.computeWaiting(this.getCurrentStep());
      return;
    }

    this.moveToNode(node.nextNodeId ?? null);
  }

  /**
   * ターゲットIDへ移動（ステップIDまたはノードIDのどちらも対応）
   * 1. 現在のノード内にターゲットIDを持つステップがあれば、そのステップへ移動
   * 2. シナリオ内にターゲットIDを持つノードがあれば、そのノードへ移動
   * 3. どちらもなければnullとして完了扱い
   */
  private moveToTarget(targetId: string | null | undefined): void {
    if (!targetId) {
      this.moveToNode(null);
      return;
    }

    // 1. 現在のノード内でステップIDを検索
    const currentNode = this.getCurrentNode();
    if (currentNode?.steps) {
      const stepIndex = currentNode.steps.findIndex((s) => s.id === targetId);
      if (stepIndex >= 0) {
        this.state = {
          ...this.state,
          currentStepIndex: stepIndex,
        };
        this.waiting = this.computeWaiting(currentNode.steps[stepIndex]);
        return;
      }
    }

    // 2. ノードIDとして検索
    const targetNode = this.scenario.nodes.find((n) => n.id === targetId);
    if (targetNode) {
      this.moveToNode(targetId);
      return;
    }

    // 3. 他のノード内のステップIDとして検索（ノード間ステップジャンプ）
    for (const node of this.scenario.nodes) {
      if (node.steps) {
        const stepIndex = node.steps.findIndex((s) => s.id === targetId);
        if (stepIndex >= 0) {
          this.state = {
            ...this.state,
            currentNodeId: node.id,
            currentStepIndex: stepIndex,
          };
          this.completed = false;
          this.waiting = this.computeWaiting(node.steps[stepIndex]);
          return;
        }
      }
    }

    // 見つからない場合は警告を出して次のステップへ進む
    console.warn(`[StepEngine] Target "${targetId}" not found as step or node ID. Moving to next step.`);
    this.incrementStep();
  }

  private moveToNode(nodeId: string | null): void {
    this.state = {
      ...this.state,
      currentNodeId: nodeId,
      currentStepIndex: 0,
    };

    if (!nodeId) {
      this.setCompleted();
      return;
    }

    const node = this.getCurrentNode();
    if (!node || !node.steps || node.steps.length === 0) {
      this.moveToNode(node?.nextNodeId ?? null);
      return;
    }

    this.completed = false;
    this.waiting = this.computeWaiting(node.steps[0]);
  }

  private setCompleted(): void {
    this.completed = true;
    this.waiting = null;
  }

  private computeWaiting(step: StepSchema | null): WaitingState | null {
    if (!step) {
      return null;
    }
    // branchステップは advance() で自動評価されるため、待機状態を返さない
    if (step.type === 'branch') {
      return null;
    }
    if (step.type === 'minigame') {
      return { type: 'minigame', step };
    }
    if (step.type === 'spot_gate') {
      const attempts = this.getSpotGateAttempts(step.id);
      const maxAttempts = typeof (step as any).maxAttempts === 'number' ? (step as any).maxAttempts : null;
      return {
        type: 'spot_gate',
        step,
        attempts,
        remainingAttempts: maxAttempts === null ? null : Math.max(0, maxAttempts - attempts)
      };
    }
    if (step.type === 'free_input') {
      return { type: 'free_input', step };
    }
    if (step.type === 'line_dialog') {
      return { type: 'line_dialog', step };
    }
    if (step.type === 'reward') {
      return { type: 'reward', step };
    }
    // Phase A-h: dialogステップにchoicesがある場合は待機状態にする
    // ただし、linesがある場合はlines終了後にadvance()で待機状態を設定する
    if (step.type === 'dialog') {
      const dialogStep = step as DialogStep;
      const lines = Array.isArray((dialogStep as any).lines)
        ? ((dialogStep as any).lines as any[])
        : null;
      const choices = (dialogStep as any).choices as Array<{
        text: string;
        goto?: string;
        mutations?: VariableMutation[];
        condition?: unknown;
      }> | undefined;
      // linesがない場合のみ、初期状態で待機状態にする
      // R3: ここでも condition で絞る（selectDialogChoice と同一フィルタ）。
      if (choices && choices.length > 0 && (!lines || lines.length === 0)) {
        return {
          type: 'dialog_choice',
          step: dialogStep,
          choices: filterChoicesByCondition(choices, this.state),
        };
      }
    }
    return null;
  }

  private getSpotGateAttempts(stepId: string): number {
    const value = this.state.flags[getSpotGateAttemptFlagKey(stepId)];
    return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
  }

  private ensureWaiting<T extends WaitingState>(type: T['type']): T {
    if (!this.waiting || this.waiting.type !== type) {
      throw new Error(`Waiting state ${type} expected`);
    }
    return this.waiting as T;
  }

  private getCurrentNode(): ScenarioNode | null {
    if (!this.state.currentNodeId) {
      return null;
    }
    return (
      this.scenario.nodes.find((node) => node.id === this.state.currentNodeId) ?? null
    );
  }

  private getCurrentStep(): StepSchema | null {
    const node = this.getCurrentNode();
    if (!node) {
      return null;
    }
    return node.steps?.[this.state.currentStepIndex] ?? null;
  }
}

function getSpotGateAttemptFlagKey(stepId: string): string {
  return `__spotGateAttempts:${stepId}`;
}

/**
 * P8-1: spot_gate の解答照合（純関数・副作用なし）。
 * submitSpotGateAnswer と同一のロジックをここに一本化する（SSoT）。
 * UI 層（承認ビート）が「正解になる解答か」を先読みするために export する。
 * 実際の進行・変数変更・attempts 加算は従来どおり submitSpotGateAnswer だけが行う。
 */
export function evaluateSpotGateAnswer(step: StepSchema, answer: string): boolean {
  const answerNorm = normalizeSpotGateAnswer(answer, (step as any).normalize);
  const expectedAnswers = ((step as any).expectedAnswers ?? []) as string[];
  const matchMode = ((step as any).matchMode ?? 'exact') as 'exact' | 'contains';
  return expectedAnswers.some((expected) => {
    const expectedNorm = normalizeSpotGateAnswer(expected, (step as any).normalize);
    if (!expectedNorm) return false; // 空の期待値で includes('')が常時trueになる事故を防ぐ
    return matchMode === 'contains'
      ? answerNorm.includes(expectedNorm)
      : answerNorm === expectedNorm;
  });
}

function getRouteMapHotspotFlagKey(spotId: string, hotspotId: string): string {
  return `__routeMapHotspot:${spotId}:${hotspotId}`;
}

function normalizeSpotGateAnswer(
  value: string,
  normalize?: {
    trim?: boolean;
    kana?: boolean;
    caseInsensitive?: boolean;
    removeSpaces?: boolean;
  }
): string {
  const options = {
    trim: true,
    kana: false,
    caseInsensitive: true,
    removeSpaces: true,
    ...(normalize ?? {})
  };

  let result = String(value ?? '').normalize('NFKC');
  if (options.trim) {
    result = result.trim();
  }
  if (options.removeSpaces) {
    result = result.replace(/[\s\u3000]+/g, '');
  }
  if (options.kana) {
    result = result.replace(/[\u3041-\u3096]/g, (ch) =>
      String.fromCharCode(ch.charCodeAt(0) + 0x60)
    );
  }
  if (options.caseInsensitive) {
    result = result.toLocaleLowerCase('ja-JP');
  }
  return result;
}

function matchesMinigameResultMapping(
  when: {
    status?: 'success' | 'fail' | 'partial';
    minScore?: number;
    resultKey?: string;
    equals?: string | number | boolean;
  },
  result: Record<string, unknown>
): boolean {
  if (when.status) {
    const status = normalizeMinigameStatus(result.status);
    if (status !== when.status) {
      return false;
    }
  }

  if (typeof when.minScore === 'number') {
    const score = Number(result.score);
    if (!Number.isFinite(score) || score < when.minScore) {
      return false;
    }
  }

  if (when.resultKey && Object.prototype.hasOwnProperty.call(when, 'equals')) {
    return getNestedResultValue(result, when.resultKey) === when.equals;
  }

  return true;
}

function normalizeMinigameStatus(value: unknown): 'success' | 'fail' | 'partial' | undefined {
  if (value === 'success') {
    return 'success';
  }
  if (value === 'partial') {
    return 'partial';
  }
  if (value === 'fail' || value === 'failure') {
    return 'fail';
  }
  return undefined;
}

function getNestedResultValue(source: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((current, key) => {
    if (!current || typeof current !== 'object') {
      return undefined;
    }
    return (current as Record<string, unknown>)[key];
  }, source);
}
